/* Progressive enhancement. Buying stays a native Shopify form POST, including without JS. */
if (!customElements.get('phoenix-product')) {
  class PhoenixProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.variant = this.querySelector('[data-px-variant]');
      this.quantity = this.querySelector('[data-px-quantity]');
      this.image = this.querySelector('.px-main-image');
      const showImageError = () => {
        if (this.image) this.image.hidden = true;
        const fallback = this.querySelector('[data-px-image-error]');
        if (fallback) fallback.hidden = false;
      };
      this.showImageError = showImageError;
      this.image?.addEventListener('error', showImageError, { signal });
      if (this.image?.complete && this.image.naturalWidth === 0) showImageError();
      this.add = this.querySelector('[data-px-add]');
      this.variant?.addEventListener('change', () => this.updateVariant(), { signal });
      this.quantity?.addEventListener('input', () => this.updateQuantityButtons(), { signal });
      this.querySelector('[data-px-minus]')?.addEventListener('click', () => this.stepQuantity(-1), { signal });
      this.querySelector('[data-px-plus]')?.addEventListener('click', () => this.stepQuantity(1), { signal });
      this.querySelectorAll('[data-px-image]').forEach(link => link.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.setImage(link.dataset.pxImage, link.dataset.pxAlt);
        this.querySelectorAll('[data-px-image]').forEach(item => item.removeAttribute('aria-current'));
        link.setAttribute('aria-current', 'true');
      }, { signal }));
      this.querySelector('.px-form')?.addEventListener('submit', () => {
        // Do not disable named controls or intercept the native request.
        this.add?.setAttribute('aria-busy', 'true');
      }, { signal });
      window.addEventListener('pageshow', () => this.add?.removeAttribute('aria-busy'), { signal });
      this.updateQuantityButtons();
      const sticky = this.querySelector('[data-px-sticky]');
      const purchase = this.querySelector('.px-purchase-panel');
      if (sticky && purchase && 'IntersectionObserver' in window) {
        this.purchaseVisible = true;
        this.footerVisible = false;
        const updateSticky = () => { sticky.hidden = this.purchaseVisible || this.footerVisible; };
        this.observer = new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (entry.target === purchase) this.purchaseVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
            else this.footerVisible = entry.isIntersecting;
          }
          updateSticky();
        });
        this.observer.observe(purchase);
        const footer = document.querySelector('.ts-footer');
        if (footer) this.observer.observe(footer);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
    setImage(src, alt) {
      if (!src) return;
      if (!this.image) {
        this.image = document.createElement('img');
        this.image.className = 'px-main-image';
        this.image.width = 990;
        this.image.height = 990;
        this.image.addEventListener('error', this.showImageError, { signal: this.controller.signal });
        this.querySelector('.px-stage__image')?.append(this.image);
      }
      this.image.removeAttribute('srcset');
      this.image.hidden = false;
      this.querySelectorAll('.px-image-fallback').forEach(node => { node.hidden = true; });
      this.image.src = src;
      this.image.alt = alt || '';
    }
    updateVariant() {
      const option = this.variant?.selectedOptions[0];
      if (!option) return;
      const data = option.dataset;
      this.dataset.modelDetails = data.modelDetails || 'true';
      if (data.displayTitle) this.querySelectorAll('[data-px-model-title]').forEach(node => { node.textContent = data.displayTitle; });
      this.querySelectorAll('[data-px-product-link]').forEach(link => {
        const url = new URL(link.href);
        url.searchParams.set('variant', option.value);
        link.href = url.pathname + url.search + url.hash;
      });
      this.querySelectorAll('[data-px-price],[data-px-sticky-price]').forEach(node => { node.textContent = data.price; });
      const compare = this.querySelector('[data-px-compare]');
      if (compare) { compare.textContent = data.compare; compare.hidden = !data.compare; }
      if (this.add) {
        this.add.disabled = data.available !== 'true';
        this.add.removeAttribute('aria-busy');
        this.querySelector('[data-px-add-text]').textContent = this.add.disabled ? this.dataset.soldLabel : this.dataset.addLabel;
      }
      if (this.quantity) {
        this.quantity.min = data.min || '1';
        this.quantity.step = data.step || '1';
        if (data.max) this.quantity.max = data.max; else this.quantity.removeAttribute('max');
        this.quantity.value = this.quantity.min;
        const rule = this.querySelector('[data-px-rule]');
        if (rule) {
          rule.hidden = Number(data.min || 1) <= 1 && Number(data.step || 1) <= 1 && !data.max;
          this.querySelector('[data-px-rule-value]').textContent = [data.min || '1', data.step || '1', data.max].filter(Boolean).join(' / ');
        }
        this.updateQuantityButtons();
      }
      if (data.image) this.setImage(data.image, data.imageAlt);
      else if (this.dataset.modelDetails === 'false' && this.image) {
        this.image.hidden = true;
        const fallback = this.querySelector('[data-px-image-error]');
        if (fallback) fallback.hidden = false;
      }
      this.querySelectorAll('[data-px-image]').forEach(link => {
        if (data.image && link.dataset.pxImage === data.image) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      if (this.dataset.productPage === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    }
    stepQuantity(direction) {
      if (!this.quantity) return;
      if (direction > 0) this.quantity.stepUp(); else this.quantity.stepDown();
      this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.updateQuantityButtons();
    }
    updateQuantityButtons() {
      if (!this.quantity) return;
      const value = Number(this.quantity.value);
      const min = Number(this.quantity.min || 1);
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const minus = this.querySelector('[data-px-minus]');
      const plus = this.querySelector('[data-px-plus]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }
  }
  customElements.define('phoenix-product', PhoenixProduct);
}
